// --- React
import React from 'react';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import ReactHtmlParser from 'react-html-parser';
// --- Utilities
import {legalStatusCMVerbiage, formatMemoTitleDisplay} from '../../utils';
import DOMPurify from 'dompurify';
import {datastageApi} from "../../../../api";
import {useLoading} from "../../../../utils/hooks/useLoading";
import {saveAs} from 'file-saver';

// CONSTANTS for legal header and notice
const legalHeader = `PRIVILEGED AND CONFIDENTIAL<br />
SUBJECT TO ATTORNEY-CLIENT AND ATTORNEY WORK PRODUCT PRIVILEGES<br />
DO NOT DUPLICATE - DO NOT CIRCULATE<br /><br />`;
const legalNotice = `This memorandum does not include obligations under United States guild agreements which may affect Disney&apos;s rights to exploit this property, such as the director&apos;s editing rights under the DGA Agreement. Please contact Labor Relations if you have any questions regarding such obligations.`;

/**
 * ClearanceMemoCard Component
 *
 * This component renders a clearance memo for media distribution rights.
 * It displays memo details, legal disclaimers, and attachments while providing
 * a PDF download link.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.treeData - Array containing memo sections with content and attachments
 * @param {Object} props.memoDetails - Details of the selected product/clearance memo
 * @param {Object} props.title - Title information including name and release date
 * @param {Object} props.sectionRefs - References for scrolling to specific memo sections
 * @returns {JSX.Element} A formatted clearance memo display
 */
export function ClearanceMemoCard({treeData, memoDetails, title, sectionRefs}) {

    const {setLoadingTrue, setLoadingFalse} = useLoading();

    const downloadClearanceMemo = async () => {
        setLoadingTrue();
        const res = await datastageApi.exportClearanceMemoPDF(
            title?.foxFinancialTitleId, title?.id, true, (memoDetails?.productTitle || memoDetails?.title || title?.title))
        saveAs(res.data, title?.title + '.pdf');
        setLoadingFalse();
    };

    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    function renderSection(section, downloadAttachment, level = 1) {
        if (!section?.clearanceMemoSection?.isPublic) return null;
        const sectionId = section?.clearanceMemoSection?.id ?? `fallback-id-${index}`;
        return (
            <div
                key={`section-${section?.clearanceMemoSection?.id ?? Math.random()}`}
                style={{ marginLeft: 20 * level }}
                ref={el => (sectionRefs.current[sectionId] = el)}
            >
                <br />
                {ReactHtmlParser(DOMPurify.sanitize(section?.clearanceMemoSection?.body))}
                <br />
                {section?.clearanceMemoSection?.attachments?.map((doc, docIndex) => (
                    <a
                        key={`attachment-${doc?.rightslineId ?? docIndex}-${docIndex}`}
                        onClick={e => {
                            e.preventDefault();
                            downloadAttachment(doc?.rightslineId, doc?.attachmentName);
                        }}
                        style={{
                            fontSize: 16,
                            display: 'block',
                            marginBottom: 4,
                            color: '#3611c8',
                        }}
                    >
                        {doc.attachmentName}
                    </a>
                ))}
                {Array.isArray(section?.children) &&
                    section.children.map(child =>
                        renderSection(child, downloadAttachment, level + 1)
                    )
                }
            </div>
        );
    }


    return (
        <>
            <div style={{padding: 20, background: '#fff'}}>
                {/* Link to PDF version of the Clearance Memo */}
                {memoDetails?.productId && (
                    <a
                        onClick={downloadClearanceMemo}
                        style={{
                            fontSize: 14,
                            color: '#3611c8',
                            position: 'absolute',
                            top: 40,
                            right: 30,
                            cursor: 'pointer',
                        }}
                    >
                        PDF
                    </a>
                )}

                {/* Header: Clearance Memo Title */}
                <Typography variant="T60" style={{textAlign: 'center'}}>
                    All Media Distribution Rights
                </Typography>

                {/* Subheader: Title */}
                <Typography variant="T30" style={{textAlign: 'center'}}>
                    {formatMemoTitleDisplay(title?.title)}
                </Typography>

                {/* Legal Disclaimer */}
                <Typography
                    variant="T20"
                    style={{textAlign: 'center', textDecoration: 'underline'}}
                >
                    {ReactHtmlParser(DOMPurify.sanitize(legalHeader))}
                </Typography>

                {/* Additional Legal Notice */}
                <Typography variant="T20" style={{paddingLeft: 10, textAlign: 'left'}}>
                    {ReactHtmlParser(DOMPurify.sanitize(legalNotice))}
                </Typography>

                {/* Legal Status */}
                <Typography
                    variant="T20"
                    style={{fontSize: '14px', textAlign: 'left', paddingLeft: 10, color: 'red'}}
                >
                    {legalStatusCMVerbiage[title?.legalStatusId ?? 0]}
                </Typography>

                {treeData?.length > 0 &&
                    treeData.map((section, index) => {
                        const sectionId = section?.clearanceMemoSection?.id ?? `fallback-id-${index}`;
                        if (!section?.clearanceMemoSection?.isPublic) return null;
                        return (
                            <div
                                key={`section-${sectionId}-${index}`}
                                ref={el => (sectionRefs.current[sectionId] = el)}
                                style={{paddingBottom: 24}}
                            >
                                {/* Parent Section Body */}
                                <div>
                                    {
                                        (!section?.clearanceMemoSection?.body?.trim() &&
                                            Array.isArray(section?.children) &&
                                            section.children.some(child => child?.clearanceMemoSection?.isPublic)
                                        )
                                            ? (
                                                <span style={{fontWeight: 'bold', textDecoration: 'underline'}}>
                {section?.clearanceMemoSection?.title?.toUpperCase()}
            </span>
                                            )
                                            : ReactHtmlParser(DOMPurify.sanitize(section?.clearanceMemoSection?.body))
                                    }
                                </div>
                                {/* Parent Attachments */}
                                {section?.clearanceMemoSection?.attachments?.map((doc, docIndex) => (
                                    <a
                                        key={`attachment-${doc?.rightslineId ?? docIndex}-${docIndex}`}
                                        onClick={e => {
                                            e.preventDefault();
                                            downloadAttachment(doc?.rightslineId, doc?.attachmentName);
                                        }}
                                        style={{
                                            fontSize: 16,
                                            display: 'block',
                                            marginBottom: 4,
                                            color: '#3611c8',
                                        }}
                                    >
                                        {doc.attachmentName}
                                    </a>
                                ))}

                                {/* Child Sections */}
                                {Array.isArray(section?.children) &&
                                    section.children.map(child =>
                                        renderSection(child, downloadAttachment, 1)
                                    )
                                }
                            </div>
                        );
                    })}
            </div>
        </>
    );
}
